"use client";

import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

/* Tiny slider bar */
function Bar({ pct, color = "#FF3366" }: { pct: number; color?: string }) {
  return (
    <div className="h-[3px] rounded-full bg-white/[0.06] mt-0.5 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function ProductShowcase() {
  const ref = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(true);

  useEffect(() => {
    // Depend on `active`: the dot is conditionally rendered, so a fresh tween
    // must start when it re-mounts (with [] deps, toggling OFF leaked a tween
    // on a detached node and toggling ON left the dot frozen).
    if (!active || !dotRef.current) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const tl = gsap.to(dotRef.current, {
      motionPath: {
        path: [
          { x: 24, y: 0 }, { x: 0, y: -24 },
          { x: -24, y: 0 }, { x: 0, y: 24 }, { x: 24, y: 0 },
        ],
        curviness: 1.5,
      },
      duration: 4, repeat: -1, ease: "sine.inOut",
    });
    return () => { tl.kill(); };
  }, [active]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from("[data-showcase]", {
        scrollTrigger: { trigger: ref.current, start: "top 80%", toggleActions: "play none none none" },
        y: 50, autoAlpha: 0, duration: 0.7, ease: "power2.out",
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  /* ── panel colors mirroring extension's Neon Vinyl theme ── */
  const C = { pink: "#FF3366", amber: "#FF9F1C", teal: "#2EC4B6", surface: "rgba(10,10,16,0.96)" };

  return (
    <section ref={ref} className="relative py-16 md:py-24 px-6">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent" />
      <div className="relative max-w-[1100px] mx-auto">
        <div className="text-center mb-10">
          <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#2EC4B6] mb-3">The Extension</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">Lives on top of your music.</h2>
          <p className="text-sm text-[#b3b3b3] mt-3 max-w-md mx-auto">A floating panel for YouTube, Spotify Web and Apple Music. Drag it, collapse it, tweak everything in real time.</p>
        </div>

        <div
          data-showcase
          role="img"
          aria-label="Mockup of the 8Dfy floating control panel over a YouTube video, showing preset, speed, intensity, bass, clarity and reverb controls"
          className="relative mx-auto max-w-[820px]"
        >
          {/* ── Browser chrome ── */}
          <div className="rounded-t-xl border border-b-0 border-white/[0.08] bg-[#161620] px-4 py-2.5 flex items-center gap-3">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
            </div>
            <div className="flex-1 flex items-center h-6 rounded-md bg-white/[0.06] px-3">
              <svg className="w-3 h-3 text-white/50 mr-2 shrink-0" viewBox="0 0 16 16" fill="none"><path d="M8 1a4 4 0 0 1 4 4v1h1a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h1V5a4 4 0 0 1 4-4z" stroke="currentColor" strokeWidth="1.2" /></svg>
              <span className="text-[10px] text-white/50 truncate">music.youtube.com/watch?v=your-playlist</span>
            </div>
          </div>

          {/* ── Video area ── */}
          <div className="relative rounded-b-xl border border-white/[0.08] bg-[#0f0f17] overflow-hidden" style={{ aspectRatio: "16/9" }}>
            {/* Fake video */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-white/[0.06] flex items-center justify-center mx-auto mb-2">
                  <svg className="w-5 h-5 text-white/50 ml-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                </div>
                <p className="text-[10px] text-white/50">Your favorite song playing...</p>
              </div>
            </div>
            {/* YouTube progress */}
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-black/60 to-transparent flex items-end px-3 pb-1.5">
              <div className="flex-1 h-[3px] rounded-full bg-white/10 overflow-hidden">
                <div className="w-[35%] h-full bg-red-600 rounded-full" />
              </div>
            </div>
          </div>

          {/* ════════════════════════════════════════
               8Dfy panel — EXACT replica of the extension
               Sibling of the video area (not a child): inside the 16:9
               overflow-hidden box it was ~60% top-clipped on phones. On
               mobile it flows below the browser mock; on md+ it overlays
               in the same spot as before (data-showcase is relative).
               ════════════════════════════════════════ */}
          <div className="mx-auto mt-4 w-[240px] md:absolute md:bottom-10 md:right-2.5 md:mt-0 md:mx-0 md:w-[220px]">
              <div className="rounded-xl overflow-hidden border border-white/[0.08]" style={{ background: C.surface, backdropFilter: "blur(20px)", boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}>

                {/* ── Header ── */}
                <div className="flex items-center px-3 py-2 border-b border-white/[0.05]" style={{ background: "linear-gradient(180deg, rgba(255,51,102,0.04) 0%, transparent 100%)" }}>
                  <span className="flex-1 text-[8px] font-bold uppercase tracking-[0.4px] bg-gradient-to-r from-[#FF3366] to-[#FF9F1C] bg-clip-text text-transparent">Audio Spatializer</span>
                  {/* Mouse-only easter egg — parent is role="img", so keep it out of the tab order */}
                  <button tabIndex={-1} onClick={() => setActive(!active)} className={`text-[7px] font-bold px-2 py-0.5 rounded-md border mr-1 transition-all ${active ? "bg-[#FF3366]/20 border-[#FF3366]/50 text-[#FF6B8A]" : "border-white/10 text-white/50"}`}>{active ? "ON" : "OFF"}</button>
                  <span className="text-white/50 text-[10px] cursor-default select-none">&minus;</span>
                </div>

                {/* ── Visualizer canvas ── */}
                <div className="flex justify-center py-2.5">
                  <div className="relative w-[72px] h-[72px]">
                    <div className="absolute inset-[11px] rounded-full border border-white/[0.1]" />
                    <div className="absolute top-1/2 left-1/2 w-1 h-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/20" />
                    <span className="absolute top-1/2 left-0 -translate-y-1/2 text-[6px] text-white/50 font-medium">L</span>
                    <span className="absolute top-1/2 right-0 -translate-y-1/2 text-[6px] text-white/50 font-medium">R</span>
                    {active && <div ref={dotRef} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"><div className="w-2.5 h-2.5 rounded-full bg-[#FF3366] shadow-[0_0_8px_rgba(255,51,102,0.5)]" /></div>}
                  </div>
                </div>

                <div className="px-3 pb-2.5">
                  {/* ── SPATIAL section ── */}
                  <p className="text-[6px] font-bold uppercase tracking-[1px] text-[#FF9F1C] mb-1.5 mt-0.5 pb-0.5 border-b border-[#FF9F1C]/10">Spatial</p>

                  {/* Mode tabs — mirrors the real panel */}
                  <div className="flex gap-0.5 mb-1.5 p-0.5 rounded-md bg-white/[0.02] border border-white/[0.04]">
                    {["Standard", "Surround", "3D"].map((m, i) => (
                      <span key={m} className={`flex-1 text-center text-[5.5px] font-bold uppercase tracking-[0.2px] py-1 rounded transition-all ${i === 0 ? "bg-[#FF3366]/15 text-[#FF6B8A]" : "text-white/50"}`}>{m}</span>
                    ))}
                  </div>

                  {/* Preset dropdown */}
                  <div className="mb-1.5">
                    <span className="text-[6px] uppercase tracking-[0.3px] text-white/50 block mb-0.5">Preset</span>
                    <div className="flex items-center justify-between py-1 px-1.5 rounded border border-white/[0.07] bg-white/[0.03] text-[8px] text-white/60">
                      8D Rotation
                      <svg className="w-1.5 h-1.5 text-[#FF3366]" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z" fill="currentColor" /></svg>
                    </div>
                  </div>

                  {/* Speed */}
                  <div className="mb-1">
                    <div className="flex justify-between"><span className="text-[6px] uppercase tracking-[0.3px] text-white/50">Speed</span><span className="text-[6px] font-bold text-white/50">0.15 Hz</span></div>
                    <Bar pct={15} />
                  </div>
                  {/* 8D movement */}
                  <div className="mb-1">
                    <div className="flex justify-between"><span className="text-[6px] uppercase tracking-[0.3px] text-white/50">8D Movement</span><span className="text-[6px] font-bold text-[#FF6B8A]">Full orbit</span></div>
                    <Bar pct={100} />
                  </div>

                  {/* Checkboxes */}
                  <div className="flex gap-3 mb-2 mt-1">
                    <label className="flex items-center gap-1 text-[6px] text-white/50">
                      <span className="w-2 h-2 rounded-sm border border-white/10 bg-[#FF3366]/30 flex items-center justify-center text-[5px] text-white">&#10003;</span>
                      Interaural Delay
                    </label>
                    <label className="flex items-center gap-1 text-[6px] text-white/50">
                      <span className="w-2 h-2 rounded-sm border border-white/10 bg-[#FF3366]/30 flex items-center justify-center text-[5px] text-white">&#10003;</span>
                      Head Shadow
                    </label>
                  </div>

                  {/* ── AUTO DJ section ── */}
                  <p className="text-[6px] font-bold uppercase tracking-[1px] text-[#FF9F1C] mt-2 mb-1.5 pb-0.5 border-b border-[#FF9F1C]/10">
                    Auto DJ <span className="text-[5px] px-1 py-[1px] rounded bg-[#FF9F1C]/10 text-[#FFB34C] ml-0.5">BETA</span>
                  </p>
                  <div className="flex items-center justify-between rounded border border-[#FF9F1C]/15 bg-[#FF9F1C]/[0.05] px-2 py-1.5">
                    <span className="text-[6px] text-white/60">Beat-matched transitions</span>
                    <span className="h-3 w-6 rounded-full bg-[#FF3366]/80 p-0.5"><span className="block h-2 w-2 translate-x-3 rounded-full bg-white" /></span>
                  </div>

                  {/* ── AUDIO ENHANCEMENT section ── */}
                  <p className="text-[6px] font-bold uppercase tracking-[1px] text-[#FF9F1C] mb-1.5 pb-0.5 border-b border-[#FF9F1C]/10">Audio Enhancement</p>

                  {[{ l: "Bass", p: 40 }, { l: "Clarity", p: 50 }, { l: "Reverb", p: 25 }].map((s) => (
                    <div key={s.l} className="mb-1">
                      <div className="flex justify-between"><span className="text-[6px] uppercase tracking-[0.3px] text-white/50">{s.l}</span><span className="text-[6px] font-bold text-white/50">{s.p}%</span></div>
                      <Bar pct={s.p} />
                    </div>
                  ))}

                  {/* ── EXPORT section ── */}
                  <p className="text-[6px] font-bold uppercase tracking-[1px] text-[#FF9F1C] mt-2 mb-1.5 pb-0.5 border-b border-[#FF9F1C]/10">
                    Export <span className="text-[5px] font-extrabold px-1 py-[1px] rounded bg-gradient-to-r from-[#FF3366] to-[#FF9F1C] text-white ml-0.5 align-middle">PRO</span>
                  </p>

                  {/* Format dropdown */}
                  <div className="mb-1.5">
                    <span className="text-[6px] uppercase tracking-[0.3px] text-white/50 block mb-0.5">Format</span>
                    <div className="flex items-center justify-between py-1 px-1.5 rounded border border-white/[0.07] bg-white/[0.03] text-[8px] text-white/60">
                      WAV (Lossless)
                      <svg className="w-1.5 h-1.5 text-[#FF3366]" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z" fill="currentColor" /></svg>
                    </div>
                  </div>

                  {/* Record button + timer */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center justify-center gap-1 py-1 rounded border border-white/[0.07] bg-white/[0.03] text-[7px] font-semibold text-white/50">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                      Record
                    </div>
                    <span className="text-[7px] text-white/50 font-mono">0:00</span>
                  </div>
                </div>
              </div>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap justify-center gap-2 mt-6">
          {["YouTube + Spotify + Apple Music", "Auto DJ", "Real-time visualizer", "Zero latency", "Works in background tabs"].map((tag) => (
            <span
              key={tag}
              className="text-[10px] font-bold uppercase tracking-[1.4px] px-3 py-1.5 rounded-full text-[#b3b3b3]"
              style={{ border: "1px solid #2a2a3a" }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
